import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Pin } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";

const NoticeImages = ({ ids = [], testKey }) => {
  if (!ids || ids.length === 0) return null;
  return (
    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`${testKey}-images`}>
      {ids.map((id) => (
        <a
          key={id}
          href={`${API_BASE}/files/${id}`}
          target="_blank"
          rel="noreferrer"
          className="block rounded-md overflow-hidden border border-border hover:opacity-90 transition-opacity"
          data-testid={`${testKey}-image-${id}`}
        >
          <img
            src={`${API_BASE}/files/${id}`}
            alt=""
            loading="lazy"
            className="w-full h-32 object-cover"
          />
        </a>
      ))}
    </div>
  );
};

const Information = () => {
  usePageTitle("Information & Notices");
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/notices").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const pinned = items.filter((i) => i.pinned);
  const others = items.filter((i) => !i.pinned);

  return (
    <div className="container-le section-pad" data-testid="information-page">
      <div className="max-w-2xl mb-10">
        <div className="text-xs uppercase tracking-[0.25em] text-secondary mb-3">Notices</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight">Information</h1>
        <p className="text-muted-foreground mt-4 leading-relaxed">
          Township notices, policies, and announcements. Pinned items are at the top.
        </p>
      </div>

      {pinned.length > 0 && (
        <div className="space-y-4 mb-10">
          {pinned.map((n) => (
            <div key={n.id} className="border-l-2 border-accent bg-muted/40 p-6 rounded-r-md" data-testid={`pinned-notice-${n.id}`}>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-accent mb-2">
                <Pin size={12} /> Pinned · {n.category}
              </div>
              <h2 className="font-heading text-xl font-semibold">{n.title}</h2>
              <p className="mt-3 whitespace-pre-wrap text-foreground/85 leading-relaxed">{n.body}</p>
              <NoticeImages ids={n.image_file_ids} testKey={`pinned-notice-${n.id}`} />
            </div>
          ))}
        </div>
      )}

      <Accordion type="multiple" className="border border-border rounded-xl bg-white divide-y divide-border" data-testid="notices-accordion">
        {others.map((n) => (
          <AccordionItem key={n.id} value={n.id} className="border-none px-5" data-testid={`notice-item-${n.id}`}>
            <AccordionTrigger className="text-left hover:no-underline">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-secondary">{n.category}</div>
                <div className="font-heading text-lg font-semibold">{n.title}</div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <p className="whitespace-pre-wrap text-foreground/85 leading-relaxed pb-2">{n.body}</p>
              <NoticeImages ids={n.image_file_ids} testKey={`notice-${n.id}`} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default Information;
